import * as React from "react";
import {Box, Card, CardContent, Typography} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import EuroIcon from "@mui/icons-material/Euro";
import {useTranslation} from "react-i18next";

interface PayrollRunSummaryProps {
    employeeCount: number;
    totalGross: number;
    totalTax: number;
    totalPensionEmp: number;
    totalPensionEmpr: number;
    totalBankNet: number;
    totalEmployerCost: number;
    formatEUR: (value: number | null | undefined) => string;
}

interface SummaryCardDef {
    icon: React.ReactElement;
    label: string;
    value: string;
    subtitle: string;
}

function SummaryCard({icon, label, value, subtitle}: SummaryCardDef): React.ReactElement {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 1,
                flex: "1 1 180px",
                minWidth: 0,
            }}
        >
            <CardContent>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                    {icon}
                    <Typography sx={{fontSize: "0.85rem", color: "text.secondary"}}>
                        {label}
                    </Typography>
                </Box>
                <Typography sx={{fontSize: "1.5rem", fontWeight: 600}} noWrap>
                    {value}
                </Typography>
                <Typography sx={{fontSize: "0.75rem", color: "text.disabled"}}>
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default function PayrollRunSummary({
    employeeCount,
    totalGross,
    totalTax,
    totalPensionEmp,
    totalPensionEmpr,
    totalBankNet,
    totalEmployerCost,
    formatEUR,
}: PayrollRunSummaryProps): React.ReactElement {
    const {t} = useTranslation();
    const pensionPct = totalGross > 0 ? Math.round((totalPensionEmp / totalGross) * 100) : 0;

    const cards: SummaryCardDef[] = [
        {
            icon: <GroupIcon sx={{color: "text.secondary"}}/>,
            label: t('payroll.run.summary.employees'),
            value: String(employeeCount),
            subtitle: t('payroll.run.summary.headcount'),
        },
        {
            icon: <TrendingUpIcon sx={{color: "success.main"}}/>,
            label: t('payroll.run.table.header.finalGross'),
            value: formatEUR(totalGross),
            subtitle: t('payroll.run.summary.totalFinalGross'),
        },
        {
            icon: <PersonIcon sx={{color: "info.main"}}/>,
            label: t('payroll.run.summary.employeePension'),
            value: formatEUR(totalPensionEmp),
            subtitle: t('payroll.run.summary.percentOfGross', {percent: pensionPct}),
        },
        {
            icon: <ReceiptLongIcon sx={{color: "warning.main"}}/>,
            label: t('payroll.run.summary.incomeTax'),
            value: formatEUR(totalTax),
            subtitle: t('payroll.run.summary.totalTax'),
        },
        {
            icon: <AccountBalanceIcon sx={{color: "primary.main"}}/>,
            label: t('payroll.run.table.header.netToBank'),
            value: formatEUR(totalBankNet),
            subtitle: t('payroll.run.summary.totalNetPay'),
        },
        {
            icon: <BusinessIcon sx={{color: "secondary.main"}}/>,
            label: t('payroll.run.summary.employerPension'),
            value: formatEUR(totalPensionEmpr),
            subtitle: t('payroll.run.summary.companyContribution'),
        },
        {
            icon: <EuroIcon sx={{color: "text.secondary"}}/>,
            label: t('payroll.run.summary.totalEmployerCost'),
            value: formatEUR(totalEmployerCost),
            subtitle: t('payroll.run.summary.totalCostOfEmployer'),
        },
    ];

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
            {cards.map((card) => (
                <SummaryCard key={card.label} {...card} />
            ))}
        </Box>
    );
}
